import React, { useContext, useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch, connect, useSelector } from 'react-redux'
import redux, { api } from '../redux/index.js'
import { selectLocale } from '../redux/locale/selectors'
import { makeSelectorUserdata } from '../redux/login/selectors.js'
import { SnackBarContext } from '../context/common.js'

import { canOpenDocument, openDocument } from '../util.js'

import { Group, Button, Waves } from '@pergas-common/pergas-components'
import FolderView from './FolderView.js'
import NameInput from './NameInput.js'
import ContactPersonDropdown from './ContactPersonDropdown.js'
import ContentTypeDropdown from './ContentTypeDropdown.js'
import TemplateRunReport, { getTemplateRunFeedback } from './TemplateRunReport.js'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-grow: 1;
`

const Holder = styled.div`
  width: 100%;
  height: calc(100% - 36px);
  display: flex;
  flex-direction: row;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: flex-end;
  background-color: #ffffff;
  border-top: 1px solid #f0f0f0;
  position: absolute;
  bottom: 0;
  right: 0;
`

const queryParams = (params) => {
  const key = params.pds_externalid ? 'eid' : 'id'
  const value = params.pds_externalid ? params.pds_externalid : params.pds_id

  return [{
    key,
    op: '=',
    value
  }]
}

const normalizeContactPerson = (item, fallbackCollectionName = '', index = 0) => {
  const normalizedId = item?.person_id ?? item?.id ?? item?.contact_person_id ?? `contact-person-${index}`
  return {
    ...item,
    id: normalizedId,
    person_id: normalizedId,
    person_collection_name: item?.person_collection_name ?? item?.collection_name ?? fallbackCollectionName,
    person_name: item?.person_name ?? item?.name ?? item?.full_name ?? item?.display_name ?? item?.email ?? 'Kontaktperson',
    role_name: item?.role_name ?? item?.role?.name ?? item?.role ?? item?.object_type_name ?? 'Kontaktperson',
    person_address: item?.person_address ?? item?.address ?? item?.street ?? '',
    person_postal_code: item?.person_postal_code ?? item?.postal_code ?? item?.zip_code ?? item?.zip ?? '',
    person_city: item?.person_city ?? item?.city ?? '',
    person_email: item?.person_email ?? item?.email ?? '',
    person_phone: item?.person_phone ?? item?.phone ?? item?.mobile ?? '',
    person_homepage: item?.person_homepage ?? item?.homepage ?? item?.website ?? item?.url ?? ''
  }
}

const SaveView = ({
  type,
  strings,
  error,
  openDisabled,
  saveDisabled,
  selectedFolderNode,
  selectedTemplateFileNode,
  userData,
  documentData,
  templateRun,
  dataLoaded,
  onCreateAndUploadTemplate,
  onCreateAndUploadMultipleTemplates
}) => {
  const locale = useSelector(selectLocale)
  const selectUserdata = useMemo(makeSelectorUserdata, [])
  const userdata = useSelector((state) => selectUserdata(state))
  const { setFeedback } = useContext(SnackBarContext)
  const dispatch = useDispatch()
  const runReport = <TemplateRunReport report={templateRun} locale={locale} onClose={() => dispatch(redux.actions.save.dismissTemplateRunReport())} />
  const [isLoading, setIsLoading] = useState(true)
  const [collectionContactPersons, setCollectionContactPersons] = useState([])
  const {
    contact,
    project
  } = documentData

  const urlParam = Object.fromEntries(new URLSearchParams(window.location.search).entries())

  // We only want to query for the resource if we have an external id to translate to a pergas id.
  const query = queryParams(urlParam)
  const templateSiteUrl = `${userData.sc_root}_admin`

  useEffect(() => {
    if (error) {
      setFeedback(locale[error.errorType])
    }
  }, [error])

  useEffect(() => {
    const feedback = getTemplateRunFeedback(templateRun, locale)
    if (feedback !== null) setFeedback(feedback, 'progressFeedback', templateRun.running)
  }, [templateRun, locale, setFeedback])

  useEffect(() => {
    if (urlParam.pds_type === 'project') {
      dispatch(redux.actions.save.fetchProjectData(query))
    } else if (urlParam.pds_type === 'contact') {
      dispatch(redux.actions.save.fetchContactData(query))
    }
  }, [])

  useEffect(() => {
    if (urlParam.pds_type !== 'contact') {
      return
    }

    const selectedCollectionId = contact?.id
    if (!selectedCollectionId) {
      console.warn('No collection id found for contact, skipping fetch of contact persons')
      setCollectionContactPersons([])
      return
    }

    let unmounted = false

    const fetchContactPersons = async () => {
      console.info('Fetching contact persons for collection id', selectedCollectionId)
      try {
        const pageSize = 500
        let offset = 0
        let hasMore = true
        let allContactPersons = []
        const contactPersonQuery = [{
          key: 'collection_id',
          op: '=',
          value: selectedCollectionId
        }]

        while (hasMore) {
          const result = await api.getContactPersons({ query: contactPersonQuery, limit: pageSize, offset })
          const batch = result?.data?.value || []
          allContactPersons = allContactPersons.concat(batch)
          offset += pageSize
          hasMore = batch.length === pageSize
        }

        console.info(`Fetched ${allContactPersons.length} contact persons for collection id ${selectedCollectionId}`)
        console.debug('Contact persons:', allContactPersons)
        if (!unmounted) {
          setCollectionContactPersons(allContactPersons)
        }
      } catch (e) {
        console.warn('failed to fetch contact persons for contact save', e)
      }
    }

    fetchContactPersons()

    return () => {
      unmounted = true
    }
  }, [urlParam.pds_type, contact?.id])

  useEffect(() => {
    if (urlParam.pds_type === 'project') {
      if (dataLoaded.project && dataLoaded.contact) {
        setIsLoading(false)
      }
    } else {
      if (dataLoaded.contact) {
        setIsLoading(false)
      }
    }
  }, [project, contact])

  const [fileName, setFileName] = useState('')
  const [contentType, setContentType] = useState('')

  const baseContactPersons = useMemo(() => {
    if (urlParam.pds_type === 'contact') {
      return collectionContactPersons.map((item, index) => normalizeContactPerson(item, contact?.name, index))
    }

    return project?.person_role || contact?.person_role || []
  }, [project, contact, collectionContactPersons, urlParam.pds_type])

  const templateUri = selectedTemplateFileNode?.uri
  // const savePath = `${userData?.sc_root}_${getSiteSuffix({ pds_id, pds_type: type })}/${selectedFolderNode?.path}`
  const savePath = `${selectedFolderNode?.path}`
  const onSaveAndOpenDone = (documentPath) => {
    return openDocument(documentPath)
  }

  const renderButtons = () => {
    const saveButtonText = selectedTemplateFileNode?.type === 'file' ? locale.save_and_open : locale.save_templates

    return (
      <ButtonContainer>
        <Group.Button>
          <Button disabled={openDisabled} onClick={() => dispatch(redux.actions.templates.openDocument(selectedTemplateFileNode, userdata))}>{locale.open}</Button>
          <Button
            disabled={saveDisabled} onClick={async () => {
              try {
                if (selectedTemplateFileNode?.type === 'file') {
                  let saveFileName = fileName
                  if (!fileName) {
                    saveFileName = selectedTemplateFileNode.name
                  }
                  await onCreateAndUploadTemplate(templateUri, saveFileName, documentData, contentType, savePath, onSaveAndOpenDone)
                } else if (selectedTemplateFileNode?.type === 'folder') {
                  await onCreateAndUploadMultipleTemplates(templateSiteUrl, selectedTemplateFileNode, documentData, savePath)
                }
              } catch (e) {
                setFeedback(e?.message || String(e))
              }
            }}
          >{templateRun?.running && !templateRun.isBatch && <Waves width={16} height={16} />}{saveButtonText}
          </Button>
        </Group.Button>
      </ButtonContainer>
    )
  }

  if (isLoading) {
    const renderLoadingComponent = () => {
      if (!urlParam.pds_id && !urlParam.pds_externalid) {
        return <span style={{ paddingLeft: '16px', fontSize: 14 }}>{strings.no_contact_or_project_selected}</span>
      } else {
        return <Waves width={24} height={24} />
      }
    }

    return (
      <Container>
        <Holder>
          <div style={{ display: 'flex', justifyContent: 'center' }}>{renderLoadingComponent()}</div>
        </Holder>
        {renderButtons()}
        {runReport}
      </Container>
    )
  }

  return (
    <>
      <Container>
        <NameInput disabled={selectedTemplateFileNode?.type !== 'file'} onChangeInput={(_fileName) => { setFileName(_fileName) }} />
        <ContactPersonDropdown
          contactPersons={baseContactPersons}
          contactAddress={contact?.address}
          contactName={contact?.name}
          onChangeInput={() => {}}
        />
        <ContentTypeDropdown disabled={selectedTemplateFileNode?.type !== 'file'} selected={selectedTemplateFileNode?.contentType} onChangeInput={(_contentType) => { setContentType(_contentType) }} />
        <FolderView pds_id={project?.id || contact?.id} type={type} />
      </Container>
      {renderButtons()}
      {runReport}
    </>
  )
}

const mapStateToProps = (state) => {
  const { strings } = state.locale
  const error = state.error
  const { selectedFolderNode } = state.folders
  const { selectedTemplateFileNode } = state.templates
  const { documentData, loaded, templateRun } = state.save

  const isSaveDisabled = () => {
    if (templateRun?.running) return true
    if (!selectedTemplateFileNode || !selectedFolderNode) return true
    if (selectedTemplateFileNode.type === 'file' || selectedTemplateFileNode.type === 'folder') {
      return false
    }
    return true
  }

  return {
    strings,
    error,
    openDisabled: !(selectedTemplateFileNode && canOpenDocument(selectedTemplateFileNode.uri)),
    saveDisabled: isSaveDisabled(),
    selected: state.selected,
    selectedFolderNode,
    selectedTemplateFileNode,
    userData: state.login.userData,
    documentData,
    templateRun,
    dataLoaded: loaded
  }
}

const mapDispatchToProps = (dispatch) => {
  const { dialog, templates, save } = redux.actions
  return {
    onCreateAndUploadTemplate: (templateUri, fileName, documentData, contentType, savePath, onSaveAndOpenDone) => {
      return dispatch(save.createAndUploadTemplate(templateUri, fileName, documentData, contentType, savePath, onSaveAndOpenDone))
    },
    onCreateAndUploadMultipleTemplates: (templateSiteUrl, selectedTemplateFileNode, documentData, savePath, onSaveDone) => {
      return dispatch(save.createAndUploadMultipleTemplates(templateSiteUrl, selectedTemplateFileNode, documentData, savePath, onSaveDone))
    },
    onShowSaveDialog: () => {
      dispatch(save.readDocumentProperties())
      dispatch(dialog.showSaveDialog())
    },
    onAttachFile: (selectedTemplateFileNode) => {
      dispatch(templates.attachFile(selectedTemplateFileNode))
    },
    onSaveAttachmentsClick: (siteItem, fileItem) => {
      dispatch(save.uploadFileAttachments(siteItem, fileItem, () => {
        const { type } = fileItem
        if (type === 'rootfolder') {
          dispatch(templates.getRootFolder(siteItem))
        } else if (type === 'folder') {
          dispatch(templates.getSubFolder(siteItem, fileItem))
        }
      }))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SaveView)
